import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { interval } from 'rxjs';
import { catchError, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ErrorHandlerService } from '../../services/error-handler/error-handler.base-service';
import { SharedContentFacade } from '../../services/shared-content/shared-content.facade';
import * as fromActions from './shared-content.actions';
import { ISharedFolderContentResponse, IUnreadResponse } from '../../models';
import { getSelectedContract } from '../account/masterdata/masterdata.selectors';
import { getDocumentSorting } from '../general/sortings';

@Injectable()
export class SharedContentEffects {
  private static readonly UNREAD_POLL_INTERVAL_MS = 60_000;

  getSharedContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GetSharedContent),
      switchMap(action =>
        this.sharedContentFacade.getSharedContent(action.params).pipe(
          map((response: ISharedFolderContentResponse) =>
            fromActions.GetSharedContentSuccess({
              response,
            })
          ),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on shared-content.effects.ts:getSharedContent$:',
              error
            );
            return [fromActions.GetSharedContentFailed({ error })];
          })
        )
      )
    )
  );

  getUnread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GetUnread),
      switchMap(() =>
        this.sharedContentFacade.getUnread().pipe(
          map((response: IUnreadResponse) => fromActions.GetUnreadSuccess({ response })),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on shared-content.effects.ts:getUnread$:',
              error
            );
            return [fromActions.GetUnreadFailed({ error })];
          })
        )
      )
    )
  );

  pollUnread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.StartPollUnread),
      switchMap(() =>
        interval(SharedContentEffects.UNREAD_POLL_INTERVAL_MS).pipe(
          map(() => fromActions.GetUnread()),
          takeUntil(this.actions$.pipe(ofType(fromActions.StopPollUnread)))
        )
      )
    )
  );

  migrateDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.MigrateDocuments),
      switchMap(action =>
        this.sharedContentFacade.migrateDocuments(action.markAsRead).pipe(
          withLatestFrom(
            this.store.select(getSelectedContract),
            this.store.select(getDocumentSorting)
          ),
          switchMap(([_, selectedContract, selectedSort]) => [
            fromActions.MigrateDocumentsSuccess(),
            fromActions.GetUnread(),
            fromActions.GetSharedContent({
              params: {
                folderId: null,
                contractId: selectedContract?.id || null,
                sortOrder: selectedSort?.direction || null,
                sortField: selectedSort?.value || null,
              },
              forced: true,
            }),
          ]),
          catchError((error: Error) => {
            this.errorHandlerService.handleError(
              '(REQUEST ERROR) on shared-content.effects.ts:migrateDocuments$:',
              error
            );
            return [fromActions.MigrateDocumentsFailed({ error })];
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private sharedContentFacade: SharedContentFacade,
    private errorHandlerService: ErrorHandlerService
  ) {}
}
